import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import {
  AlertDialog,
  AlertDialogContent,
} from '@/components/ui/alert-dialog';
import { CheckCircle, XCircle, RefreshCcw } from 'lucide-react';

export type ActionType = 'subscribe' | 'cancel' | 'reactivate';

interface ActionConfig {
  title: string;
  message: string;
  icon: React.ReactNode;
  iconBgColor: string;
  iconColor: string;
}

const actionConfigs: Record<ActionType, ActionConfig> = {
  subscribe: {
    title: 'Subscription Successful!',
    message: 'Welcome to Pro! You now have access to all premium features including unlimited video calls, HD quality, and an ad-free experience.',
    icon: <CheckCircle className="w-16 h-16" />,
    iconBgColor: 'bg-green-500/10',
    iconColor: 'text-green-500'
  },
  cancel: {
    title: 'Subscription Cancelled',
    message: "We're sorry to see you go. Your Pro features will remain active until the end of your current billing period. You can reactivate your subscription anytime from the plans page.",
    icon: <XCircle className="w-16 h-16" />,
    iconBgColor: 'bg-orange-500/10',
    iconColor: 'text-orange-500'
  },
  reactivate: {
    title: 'Subscription Reactivated!',
    message: "Welcome back! Your Pro subscription has been reactivated and will continue beyond your current billing period. Enjoy all the premium features!",
    icon: <RefreshCcw className="w-16 h-16" />,
    iconBgColor: 'bg-blue-500/10',
    iconColor: 'text-blue-500'
  }
};

interface SubscriptionSuccessModalProps {
  open: boolean;
  action: ActionType;
  onCountdownComplete?: () => void;
}

export default function SubscriptionSuccessModal({
  open,
  action,
  onCountdownComplete
}: SubscriptionSuccessModalProps) {
  const [, navigate] = useLocation();
  const [countdown, setCountdown] = useState(10);
  const config = actionConfigs[action] || actionConfigs.subscribe;

  useEffect(() => {
    if (!open) {
      setCountdown(10);
      return;
    }

    // Start countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Call callback or reload home page to get latest status
          if (onCountdownComplete) {
            onCountdownComplete();
          } else {
            window.location.href = '/';
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, onCountdownComplete]);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="bg-[#1A2332] border-gray-700 max-w-lg">
        <div className="p-4">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`${config.iconBgColor} ${config.iconColor} rounded-full p-4`}>
              {config.icon}
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-white text-center mb-4">
            {config.title}
          </h1>

          {/* Message */}
          <p className="text-gray-300 text-center mb-8 leading-relaxed">
            {config.message}
          </p>

          {/* Countdown */}
          <div className="bg-[#0E1525] rounded-lg p-6 border border-gray-700">
            <p className="text-gray-400 text-center mb-2">
              Redirecting to home page in
            </p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-5xl font-bold text-[#11B981]">
                {countdown}
              </span>
              <span className="text-gray-400 text-lg">
                {countdown === 1 ? 'second' : 'seconds'}
              </span>
            </div>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
